import React, { useState } from "react";
import useEscapeLayer from "../useEscape";
import { fullName, firstName } from "./format.mjs";
import { nowLine } from "./zones.mjs";
import { lifecyclePill, LIFECYCLE_CHOICES, enrichWords, phoneLinks, splitName } from "./railModel.mjs";
import { httpUrl } from "../Facts";
import { isConnected, isHandMarked, connectHref, connectedTitle } from "./linkedinConnect.mjs";
import useConnectedToggle from "./AlreadyConnected";

/*
 * ONE PERSON, OPENED IN PLACE (frame 1a's Shahar card, built for everyone: the
 * reality check's [12] resolution, because the drawn card left out the filled
 * states the brief requires).
 *
 * CLOSED it is one row in the People list: the name, their title (or "No title ·
 * Enrich from Apollo"), "2 records" when a duplicate is folded into it, their Lead /
 * Associated tag, ▸. OPEN it is the bordered card: Title, Email with every other
 * address and "Add another email", Phone with tap-to-call and WhatsApp, LinkedIn as
 * a readable name (the stored slug is percent-encoded: "shahar-bahat-%e2%98%84"),
 * their local time in both zones, the lifecycle pill with its menu, and the Lead /
 * Associated switch. Each written fact has a pencil; every write is PATCH
 * /contacts/:id, and its error sits beside the control that failed.
 *
 * "Open →" shows their whole record in the same panel (the shell's person stack,
 * with "← {company}" back). Not on the person already on screen: a link that opens
 * the page you are reading does nothing a person can see.
 *
 * "Already on {name}": POST /contacts/:id/emails answers 409 when another contact
 * holds the address, and its message does not name them. A colleague's address is
 * named from the company we already hold; anyone else is looked up by the address.
 *
 * Props:
 *   person   a company contact (GET /companies/:id contacts[], with local_zone)
 *   record   the shell's bundle: zones (mine), company, id (the record on screen)
 *   ops      ops.patchContact(id, body), ops.call(path, options), ops.reload,
 *            ops.openPerson(id)
 *   isPoc    boolean, the point-of-contact tag
 *   open     boolean, the details are shown
 *   onToggle () => void
 *   dupe     boolean, a suspected duplicate is folded into this row ("2 records")
 */

/** A pencil that turns its row into an input, Save (black) and Cancel. */
function useEdit(onSave) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const start = (value) => {
    setError(null);
    setDraft(value || "");
  };
  const cancel = () => {
    setDraft(null);
    setError(null);
  };
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave(draft.trim() || null);
      setDraft(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return { editing: draft !== null, draft, setDraft, busy, error, start, cancel, save };
}

function EditRow({ edit, placeholder, inputMode, className = "rec-kv__edit" }) {
  return (
    <span className={`${className} rec-stack`}>
      <span className="rec-editrow">
        <input
          className="rec-input"
          autoFocus
          aria-label={placeholder}
          placeholder={placeholder}
          inputMode={inputMode}
          value={edit.draft}
          disabled={edit.busy}
          onChange={(e) => edit.setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && edit.save()}
        />
        <button type="button" className="rec-btn rec-btn--dark rec-btn--sm" disabled={edit.busy} onClick={edit.save}>Save</button>
        <button type="button" className="rec-btn rec-btn--plain rec-btn--sm" disabled={edit.busy} onClick={edit.cancel}>Cancel</button>
      </span>
      {edit.error && <span className="rec-err">{edit.error}</span>}
    </span>
  );
}

/** Does this contact hold the address, as its email or one of its other emails? */
function holds(contact, address) {
  if (!contact) return false;
  if (String(contact.email || "").toLowerCase() === address) return true;
  return ((contact.properties && contact.properties.alt_emails) || []).some((e) => String(e).toLowerCase() === address);
}

/* An address in a narrow rail breaks before its @, never inside the domain. */
function Address({ value }) {
  const at = String(value).indexOf("@");
  if (at <= 0) return value;
  return (
    <>
      {value.slice(0, at)}
      <wbr />
      {value.slice(at)}
    </>
  );
}

/*
 * Beside the LinkedIn link: a green check when LinkedIn's own mail says they are
 * connected, else a yellow Connect that opens their profile (or a people search
 * when we have none), and "Already connected" for the invitation he accepted,
 * which no email reports. A check he set by hand carries its Undo. See
 * linkedinConnect.mjs for why "not set" is not "not connected".
 */
function LinkedInStatus({ person, company, ops }) {
  const toggle = useConnectedToggle(person, ops);
  if (isConnected(person)) {
    const title = connectedTitle(person);
    return (
      <>
        <span className="rec-li__ok" title={title} aria-label={title}>✓</span>
        {isHandMarked(person) && ops && (
          <button type="button" className="rec-link rec-li__act" disabled={toggle.busy} title="Undo “Already connected”" onClick={() => toggle.set(false)}>
            Undo
          </button>
        )}
        {toggle.error && <span className="rec-err">{toggle.error}</span>}
      </>
    );
  }
  const href = connectHref(person, company);
  return (
    <>
      {href && <a className="rec-btn rec-btn--li" href={href} target="_blank" rel="noopener noreferrer">Connect</a>}
      {ops && (
        <button type="button" className="rec-link rec-li__act" disabled={toggle.busy} onClick={() => toggle.set(true)}>
          Already connected
        </button>
      )}
      {toggle.error && <span className="rec-err">{toggle.error}</span>}
    </>
  );
}

function Pencil({ label, onClick }) {
  return (
    <button type="button" className="rec-pencil" title={`Edit ${label}`} aria-label={`Edit ${label}`} onClick={onClick}>
      ✎
    </button>
  );
}

/* The lifecycle pill and its menu. Esc closes the menu before anything under it. */
function Lifecycle({ person, company, onPick }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  useEscapeLayer(open, () => setOpen(false));
  const pill = lifecyclePill(person, company);
  const pick = async (stage) => {
    setOpen(false);
    if (stage === pill.stage) return;
    setError(null);
    try {
      await onPick(stage);
    } catch (e) {
      setError(e.message);
    }
  };
  return (
    <span className="rec-stack">
      <span className="rec-popwrap">
        <button
          type="button"
          className="rec-lifebtn"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`${firstName(person)}’s lifecycle stage: ${pill.word}`}
          onClick={() => setOpen((v) => !v)}
        >
          <span className={`rec-life rec-life--${pill.cls}`}>{pill.word}</span> {open ? "▴" : "▾"}
        </button>
        {open && (
          <div className="rec-pop rec-pop--narrow">
            <div className="rec-menu" role="menu">
              {pill.derived && <div className="rec-menu__label">Reads churned while {company.name} is churned. The stage underneath:</div>}
              {LIFECYCLE_CHOICES.map(([value, label]) => (
                <button key={value} type="button" role="menuitemradio" aria-checked={pill.stage === value} className={`rec-menu__item${pill.stage === value ? " is-on" : ""}`} onClick={() => pick(value)}>
                  <span>{label}</span>
                  {pill.stage === value && <span className="rec-menu__hint">now</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </span>
      {error && <span className="rec-err">{error}</span>}
    </span>
  );
}

export default function PersonCard({ person, record, ops, isPoc = false, open = false, onToggle = null, dupe = false }) {
  const p = person;
  const company = record.company;
  const [note, setNote] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [clash, setClash] = useState(null);
  const [switchError, setSwitchError] = useState(null);

  const patch = (body) => ops.patchContact(p.id, body);
  const nameEdit = useEdit((v) => patch(splitName(v)));
  const titleEdit = useEdit((v) => patch({ job_title: v }));
  const emailEdit = useEdit((v) => patch({ email: v }));
  const phoneEdit = useEdit((v) => patch({ phone: v }));
  const linkedinEdit = useEdit((v) => patch({ linkedin_url: v }));

  const alt = ((p.properties && p.properties.alt_emails) || []).filter((e) => typeof e === "string");
  // Their zone only: a colleague with no zone of their own has no clock, never the
  // zone of the person on screen and never Gabriel's.
  const local = nowLine({ ...record.zones, theirs: p.local_zone || null }, { place: company && company.country });
  const links = phoneLinks(p.phone);
  const onScreen = String(p.id) === String(record.id);
  const name = fullName(p) || "(no name)";
  const kindWord = p.kind === "associated" ? "Associated" : "Lead";

  const enrich = async () => {
    setEnriching(true);
    setNote(null);
    try {
      const r = await ops.call(`/contacts/${p.id}/enrich`, { method: "POST" });
      setNote(enrichWords(r));
      if (r && r.enriched) await ops.reload();
    } catch (e) {
      setNote(e.message);
    } finally {
      setEnriching(false);
    }
  };

  const addEmail = async () => {
    const address = newEmail.trim().toLowerCase();
    if (!address) return;
    setClash(null);
    // A colleague's address is named before anything is sent: we already know whose it is.
    const colleague = ((company && company.contacts) || []).find((c) => String(c.id) !== String(p.id) && holds(c, address));
    if (colleague) {
      setClash({ text: `Already on ${fullName(colleague)}`, id: colleague.id });
      return;
    }
    setEmailBusy(true);
    try {
      const r = await ops.call(`/contacts/${p.id}/emails`, { method: "POST", body: JSON.stringify({ email: address }) });
      if (r && r.added === false) setClash({ text: `Already on ${firstName(p)}`, id: null });
      else {
        setNewEmail("");
        setAdding(false);
        await ops.reload();
      }
    } catch (e) {
      // The 409 says only "already on another contact"; the address names who.
      if (/already on another contact/i.test(e.message)) {
        let holder = null;
        try {
          // The search matches names too: only a row that holds this exact address is the holder.
          const found = await ops.call(`/contacts?q=${encodeURIComponent(address)}&limit=5`);
          holder = ((found && found.contacts) || []).find((c) => holds(c, address)) || null;
        } catch {
          holder = null;
        }
        setClash(holder ? { text: `Already on ${fullName(holder)}`, id: holder.id } : { text: "Already on another contact", id: null });
      } else setClash({ text: e.message, id: null });
    } finally {
      setEmailBusy(false);
    }
  };

  const setKind = async (kind) => {
    if (p.kind === kind) return;
    setSwitchError(null);
    try {
      await patch({ kind });
    } catch (e) {
      setSwitchError(e.message);
    }
  };

  const enrichLink = (
    <button type="button" className="rec-link" disabled={enriching} onClick={enrich}>
      {enriching ? "Asking Apollo…" : "Enrich from Apollo"}
    </button>
  );

  /* ---------------------------------------------------------------- closed */
  if (!open) {
    return (
      <div className="rec-personrow" onClick={onToggle || undefined}>
        <span className="rec-strong">{name}</span>
        {dupe && <span className="rec-personrow__flag">2 records</span>}
        {p.job_title ? (
          <span className="rec-personrow__title">{p.job_title}</span>
        ) : (
          <span className="rec-personrow__none" onClick={(e) => e.stopPropagation()}>
            No title · {enrichLink}
          </span>
        )}
        {/* The tag and the arrow keep to the right edge, even when a narrow rail
            wraps them under the name. */}
        <span className="rec-personrow__end">
          {isPoc ? <span className="rec-tag rec-tag--fill">Point of contact</span> : <span className="rec-tag">{kindWord}</span>}
          {onToggle && (
            <button
              type="button"
              className="rec-personrow__arrow"
              aria-expanded={false}
              aria-label={`Show ${name}`}
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
            >
              ▸
            </button>
          )}
        </span>
        {note && (
          <span className="rec-personrow__note" onClick={(e) => e.stopPropagation()}>{note}</span>
        )}
      </div>
    );
  }

  /* ---------------------------------------------------------------- open */
  return (
    <div className="rec-person">
      <div className="rec-person__head">
        {nameEdit.editing ? (
          <EditRow edit={nameEdit} placeholder="First and last name" className="rec-person__nameedit" />
        ) : (
          <>
            <span className="rec-strong">{name}</span>
            <Pencil label="name" onClick={() => nameEdit.start(fullName({ first_name: p.first_name, last_name: p.last_name }) || "")} />
            {isPoc && <span className="rec-tag rec-tag--fill">Point of contact</span>}
            {dupe && <span className="rec-personrow__flag">2 records</span>}
            <span className="rec-personrow__spacer" />
            {!onScreen && (
              <button type="button" className="rec-link rec-link--plain" onClick={() => ops.openPerson(p.id)}>Open →</button>
            )}
            {onToggle && (
              <button type="button" className="rec-personrow__arrow" aria-expanded={true} aria-label={`Hide ${name}`} onClick={onToggle}>▾</button>
            )}
          </>
        )}
      </div>
      <div className="rec-person__body">
        <div className="rec-kv rec-kv--86 rec-kv--3">
          <span className="rec-kv__k">Title</span>
          {titleEdit.editing ? (
            <EditRow edit={titleEdit} placeholder="Title" />
          ) : (
            <>
              <span>{p.job_title || <span className="rec-sub">No title · {enrichLink}</span>}</span>
              <Pencil label="title" onClick={() => titleEdit.start(p.job_title)} />
            </>
          )}

          <span className="rec-kv__k">Email</span>
          {emailEdit.editing ? (
            <EditRow edit={emailEdit} placeholder="Email" inputMode="email" />
          ) : (
            <>
              <span className="rec-person__emails">
                <span>{p.email ? <Address value={p.email} /> : <span className="rec-sub">none</span>}</span>
                {alt.map((e) => (
                  <span key={e} className="rec-sub"><Address value={e} /></span>
                ))}
                <span>
                  <button type="button" className="rec-link rec-link--quiet" aria-expanded={adding} onClick={() => { setAdding((v) => !v); setClash(null); }}>
                    {adding ? "Cancel" : "Add another email"}
                  </button>
                </span>
              </span>
              <Pencil label="email" onClick={() => emailEdit.start(p.email)} />
            </>
          )}
          {adding && (
            <span className="rec-kv__wide rec-stack">
              <span className="rec-editrow">
                <input
                  className="rec-input"
                  autoFocus
                  inputMode="email"
                  aria-label="Another email address"
                  placeholder="another@address.com"
                  value={newEmail}
                  disabled={emailBusy}
                  onChange={(e) => { setNewEmail(e.target.value); setClash(null); }}
                  onKeyDown={(e) => e.key === "Enter" && addEmail()}
                />
                <button type="button" className="rec-btn rec-btn--dark rec-btn--sm" disabled={!newEmail.trim() || emailBusy} onClick={addEmail}>
                  {emailBusy ? "Adding…" : "Add"}
                </button>
              </span>
              {clash && (
                <span className="rec-err">
                  {clash.text}
                  {clash.id && String(clash.id) !== String(record.id) && (
                    <>
                      {" · "}
                      <button type="button" className="rec-link" onClick={() => ops.openPerson(clash.id)}>Open →</button>
                    </>
                  )}
                </span>
              )}
            </span>
          )}

          <span className="rec-kv__k">Phone</span>
          {phoneEdit.editing ? (
            <EditRow edit={phoneEdit} placeholder="Phone, with the country code" inputMode="tel" />
          ) : p.phone ? (
            <>
              <span>
                {links && links.tel ? <a href={links.tel}>{p.phone}</a> : p.phone}
                {links && links.wa && (
                  <>
                    {" · "}
                    <a href={links.wa} target="_blank" rel="noopener noreferrer">WhatsApp ↗</a>
                  </>
                )}
              </span>
              <Pencil label="phone" onClick={() => phoneEdit.start(p.phone)} />
            </>
          ) : (
            <>
              <span><button type="button" className="rec-link" onClick={() => phoneEdit.start("")}>Add phone</button></span>
              <span />
            </>
          )}

          <span className="rec-kv__k">LinkedIn</span>
          {linkedinEdit.editing ? (
            <EditRow edit={linkedinEdit} placeholder="LinkedIn profile link" inputMode="url" />
          ) : p.linkedin_url ? (
            <>
              {/* Their name, not the slug: Apollo's slugs carry emoji as %e2%98%84. */}
              <span className="rec-li">
                <a href={httpUrl(p.linkedin_url)} target="_blank" rel="noopener noreferrer">{name} ↗</a>
                <LinkedInStatus person={p} company={record && record.company} ops={ops} />
              </span>
              <Pencil label="LinkedIn" onClick={() => linkedinEdit.start(p.linkedin_url)} />
            </>
          ) : (
            <>
              <span className="rec-li">
                {enrichLink}
                <LinkedInStatus person={p} company={record && record.company} ops={ops} />
              </span>
              <Pencil label="LinkedIn" onClick={() => linkedinEdit.start("")} />
            </>
          )}

          {local && (
            <>
              <span className="rec-kv__k">Local time</span>
              <span>{local}</span>
              <span />
            </>
          )}

          <span className="rec-kv__k">Lifecycle</span>
          <Lifecycle person={p} company={company} onPick={(stage) => patch({ lifecycle_stage: stage })} />
          <span />

          <span className="rec-kv__k">Lead</span>
          <span className="rec-stack">
            <span className="rec-seg" role="group" aria-label={`${firstName(p)}: lead or associated`}>
              {[["lead", "Lead"], ["associated", "Associated"]].map(([v, l]) => (
                <button key={v} type="button" aria-pressed={p.kind === v} className={p.kind === v ? "is-on" : ""} onClick={() => setKind(v)}>
                  {l}
                </button>
              ))}
            </span>
            {switchError && <span className="rec-err">{switchError}</span>}
          </span>
          <span />
        </div>
        {note && <div className="rec-person__note">{note}</div>}
      </div>
    </div>
  );
}
